"use client";

import { useState } from "react";
import Link from "next/link";

import { track } from "@/lib/analytics/track";
import { consultListedAmount } from "@/lib/offers/fixed-offers";

const days = [
  ["Mon", "9"],
  ["Tue", "10"],
  ["Wed", "11"],
  ["Thu", "12"],
] as const;
const times = ["9:00 AM", "10:30 AM", "1:00 PM", "2:30 PM", "4:00 PM", "5:30 PM"];

/**
 * Booking panel. When a booking URL is configured, the visitor picks a time and pays.
 * Otherwise the only action is the contact form.
 */
export default function ConsultBooking({ bookingUrl }: { bookingUrl: string | null }) {
  const [day, setDay] = useState(1);
  const [time, setTime] = useState(2);
  const consultPrice = consultListedAmount();
  const bookingParams = {
    product: "consult",
    ...(consultPrice !== null ? { price: consultPrice, value: consultPrice } : {}),
    currency: "USD",
    location: "consult_booking",
  };

  if (!bookingUrl) {
    return (
      <div
        className="panel topbar flex flex-col"
        style={{ background: "#0f172a", padding: 28, gap: 18 }}
        aria-label="Request a consult"
      >
        <h2 className="h4">60-minute consult</h2>
        <p className="body">$250. Tell me what you want to look at, and I will reply with times.</p>
        <Link
          className="btn btn-primary btn-block"
          href="/contact?offer=consult"
          onClick={() => track("booking_fallback_click", { location: "consult_booking" })}
        >
          Ask for a consult
        </Link>
      </div>
    );
  }

  return (
    <div
      className="panel topbar flex flex-col"
      style={{ background: "#0f172a", padding: 28, gap: 18 }}
      aria-label="Booking"
    >
      <div className="row justify-between">
        <h2 className="h4">Pick a time</h2>
        <span className="caption">Times in Central time</span>
      </div>
      <div className="stack g8">
        <span className="label">Day</span>
        <div className="grid grid-cols-4 gap-2">
          {days.map(([dow, num], i) => {
            const on = i === day;
            return (
              <button
                key={dow}
                className={on ? "opt on" : "opt"}
                type="button"
                aria-pressed={on}
                onClick={() => setDay(i)}
                style={{ flexDirection: "column", alignItems: "center", gap: 2, padding: "10px 6px", minHeight: 44 }}
              >
                <span className="caption">{dow}</span>
                <span className="font-bold text-white">{num}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="stack g8">
        <span className="label">Time</span>
        <div className="grid grid-cols-3 gap-2">
          {times.map((t, i) => {
            const on = i === time;
            return (
              <button
                key={t}
                className={on ? "opt on" : "opt"}
                type="button"
                aria-pressed={on}
                onClick={() => setTime(i)}
                style={{ justifyContent: "center", padding: "10px 6px", fontSize: 14, fontWeight: 600, minHeight: 44 }}
              >
                {t}
              </button>
            );
          })}
        </div>
      </div>
      <a
        className="btn btn-primary btn-block"
        href={bookingUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => track("booking_click", bookingParams)}
      >
        Book and pay
      </a>
      <span className="caption">
        Booking and payment through Cal.com. You pay $250 when you book.{" "}
        <Link href="/refund-policy">Refund policy</Link>
      </span>
    </div>
  );
}
